import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { updateAssignment } from './api';
import { Upload, Loader2, ImageIcon, Trash } from 'lucide-react';

export default function Digitise() {
  const [assignment, setAssignment] = useState({ assignment_title: '', questions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editedQuestion, setEditedQuestion] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ type: ['Essay Type'], question_text: '', mcq_options: [], marks: 0, image: null });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const assignmentData = localStorage.getItem('assignmentdata');

    if (assignmentData) {
      localStorage.setItem('assignmentdata', assignmentData);
    }
    
    if (location.state && location.state.assignment) {
      try {
        const assignmentData = location.state.assignment;
        if (assignmentData.questions && assignmentData.questions.length > 0) {
          const parsedQuestions = parseQuestions(assignmentData.questions[0].question_text);
          setAssignment({
            assignment_title: assignmentData.assignment_title || "Untitled Assignment",
            questions: parsedQuestions,
            _id: assignmentData._id
          });
        } else {
          throw new Error('Invalid data structure in API response');
        }
      } catch (err) {
        console.error('Error parsing API response:', err);
        setError('Failed to parse API response. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      console.log('No valid API response found in location state');
      setLoading(false);
    }
  }, [location.state]);

  const parseQuestions = (jsonString) => {
    try {
      const cleanJsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '').trim();
      const data = JSON.parse(cleanJsonString);
      return data.assignment.questions.map(q => ({
        ...q,
        marks: q.marks || 0,
        image: q.image || null
      }));
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return [];
    }
  };

  const renderMathText = (text) => {
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/);
    return parts.map((part, i) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>;
      } else if (part.startsWith('$') && part.endsWith('$')) {
        return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>;
      } else {
        return <span key={i}>{part}</span>;
      }
    });
  };

  const handleDelete = (index) => {
    setAssignment(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedQuestion(assignment.questions[index]);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== -1) {
      setAssignment(prev => ({
        ...prev,
        questions: prev.questions.map((q, i) => i === editingIndex ? { ...editedQuestion, marks: editedQuestion.marks || 0 } : q)
      }));
    }
    setIsEditModalOpen(false);
  };

  const handleAddQuestion = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveNewQuestion = () => {
    setAssignment(prev => ({
      ...prev,
      questions: [...prev.questions, { ...newQuestion, marks: newQuestion.marks || 0 }]
    }));
    setIsAddModalOpen(false);
    setNewQuestion({ type: ['Essay Type'], question_text: '', mcq_options: [], marks: 0, image: null });
  };

  const handleImageUpload = (e, isNewQuestion = true) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isNewQuestion) {
          setNewQuestion(prev => ({ ...prev, image: reader.result }));
        } else {
          setEditedQuestion(prev => ({ ...prev, image: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const createUpdatedAssignmentObject = () => {
    const updatedAssignment = {
      assignment_title: assignment.assignment_title,
      description: "",
      due_date: null,
      questions: [
        {
          question_text: JSON.stringify({
            assignment: {
              questions: assignment.questions.map(q => ({
                type: q.type,
                question_text: q.question_text,
                mcq_options: q.mcq_options,
                marks: q.marks,
                image: q.image
              }))
            }
          })
        }
      ],
      __v: 1,
      _id: assignment._id || "temp_id"
    };

    localStorage.removeItem('assignment');
    localStorage.setItem('assignment', JSON.stringify(updatedAssignment));
    return updatedAssignment;
  };

  const handleShareAssignment = async () => {
    setIsShareModalOpen(false);
    setLoading(true);
    const updatedAssignment = createUpdatedAssignmentObject();
    console.log('Updated Assignment Object:', updatedAssignment);
    
    try {
      await updateAssignment(updatedAssignment);
      setTimeout(() => {
        setLoading(false);
        navigate('/assignments/new');
      }, 5000); // 5 seconds loader
    } catch (error) {
      console.error('Error updating assignment:', error);
      setLoading(false);
      setError('Failed to share assignment. Please try again.');
    }
  };

  const handleUploadAnswerKey = () => {
    setIsUploadModalOpen(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSaveUploadedFile = async () => {
    if (uploadedFile) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('projectName', assignment.assignment_title);

      try {
        const response = await fetch('http://localhost:5000/api/answerKeyUpload', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to upload answer key');
        }

        alert('Answer key uploaded successfully!');
        setIsUploadModalOpen(false);
        setUploadedFile(null);
      } catch (error) {
        console.error('Error uploading answer key:', error);
        alert('Error uploading answer key');
      } finally {
        setIsUploading(false);
      }
    }
  };

  function QuestionCard({ question, index }) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 shadow-sm mb-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              Question {index + 1}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({question.type[0]})
              </span>
            </h2>
            {question.image && (
              <img src={question.image} alt={`Question ${index + 1}`} className="mb-4 max-w-full h-auto rounded-lg shadow-md" />
            )}
            <div className="text-gray-600 whitespace-pre-wrap">
              {renderMathText(question.question_text)}
            </div>
            {question.type[0] === 'MCQ' && question.mcq_options && question.mcq_options.length > 0 && (
              <div className="mt-2">
                {question.mcq_options.map((option, i) => (
                  <div key={i} className="ml-4">
                    {String.fromCharCode(97 + i)}) {option}
                  </div>
                ))}
              </div>
            )}
            {question.type[0] === 'Boolean' && (
              <div className="mt-2">
                <div className="ml-4">a) True</div>
                <div className="ml-4">b) False</div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              value={question.marks || 0}
              onChange={(e) => {
                const newMarks = parseInt(e.target.value, 10);
                setAssignment(prev => ({
                  ...prev,
                  questions: prev.questions.map((q, i) => i === index ? {...q, marks: newMarks} : q)
                }));
              }}
              className="w-16 px-2 py-1 border rounded"
              placeholder="Marks"
            />
            <button
              onClick={() => handleEdit(index)}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(index)}
              className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-center mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {assignment.assignment_title || 'DIGITIZED ASSIGNMENT'}
        </h1>
        
        <button 
          onClick={handleAddQuestion}
          className="mb-6 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <span>Add Question</span>
          <span className="text-xl">+</span>
        </button>
        
        <div className="space-y-4">
          {assignment.questions && assignment.questions.length > 0 ? (
            assignment.questions.map((question, index) => (
              <QuestionCard key={index} question={question} index={index} />
            ))
          ) : (
            <div className="text-center text-gray-500 py-8">
              No questions available. Click "Add Question" to get started.
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-between items-center">
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Share Assignment with Student
          </button>
          <button 
            onClick={handleUploadAnswerKey}
            className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            Upload Answer Key
          </button>
        </div>
      </div>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Question</h2>
            <select
              value={editedQuestion.type[0]}
              onChange={(e) => setEditedQuestion({...editedQuestion, type: [e.target.value]})}
              className="w-full p-2 border rounded mb-4"
            >
              <option value="Essay Type">Essay Type</option>
              <option value="MCQ">Multiple Choice</option>
              <option value="Boolean">Boolean</option>
            </select>
            <textarea
              value={editedQuestion.question_text}
              onChange={(e) => setEditedQuestion({...editedQuestion, question_text: e.target.value})}
              className="w-full h-40 p-2 border rounded mb-4"
            />
            <input
              type="number"
              value={editedQuestion.marks || 0}
              onChange={(e) => setEditedQuestion({...editedQuestion, marks: parseInt(e.target.value, 10)})}
              className="w-full p-2 border rounded mb-4"
              placeholder="Marks"
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Image
              </label>
              {editedQuestion.image ? (
                <div className="relative">
                  <img
                    src={editedQuestion.image}
                    alt="Question"
                    className="max-w-full h-auto rounded-lg shadow-md mb-2"
                  />
                  <button
                    onClick={() => setEditedQuestion({...editedQuestion, image: null})}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    aria-label="Remove image"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImageIcon className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, false)}
                      accept="image/*"
                    />
                  </label>
                </div>
              )}
            </div>
            {editedQuestion.type[0] === 'MCQ' && (
              <div className="mb-4">
                {editedQuestion.mcq_options.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...editedQuestion.mcq_options];
                        newOptions[index] = e.target.value;
                        setEditedQuestion({...editedQuestion, mcq_options: newOptions});
                      }}
                      placeholder={`Option ${String.fromCharCode(97 + index)}`}
                      className="flex-grow p-2 border rounded"
                    />
                    <button
                      onClick={() => {
                        const newOptions = editedQuestion.mcq_options.filter((_, i) => i !== index);
                        setEditedQuestion({...editedQuestion, mcq_options: newOptions});
                      }}
                      className="ml-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      -
                    </button>
                  </div>
                ))}
                {editedQuestion.mcq_options.length < 4 && (
                  <button
                    onClick={() => {
                      const newOptions = [...editedQuestion.mcq_options, ''];
                      setEditedQuestion({...editedQuestion, mcq_options: newOptions});
                    }}
                    className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Add Option
                  </button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add New Question</h2>
            <select
              value={newQuestion.type[0]}
              onChange={(e) => setNewQuestion({...newQuestion, type: [e.target.value]})}
              className="w-full p-2 border rounded mb-4"
            >
              <option value="Essay Type">Essay Type</option>
              <option value="MCQ">Multiple Choice</option>
              <option value="Boolean">Boolean</option>
            </select>
            <textarea
              value={newQuestion.question_text}
              onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
              placeholder="Enter your question here"
              className="w-full h-40 p-2 border rounded mb-4"
            />
            <input
              type="number"
              value={newQuestion.marks}
              onChange={(e) => setNewQuestion({...newQuestion, marks: parseInt(e.target.value, 10)})}
              className="w-full p-2 border rounded mb-4"
              placeholder="Marks"
            />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Image
              </label>
              {newQuestion.image ? (
                <div className="relative">
                  <img
                    src={newQuestion.image}
                    alt="New Question"
                    className="max-w-full h-auto rounded-lg shadow-md mb-2"
                  />
                  <button
                    onClick={() => setNewQuestion({...newQuestion, image: null})}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    aria-label="Remove image"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImageIcon className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, true)}
                      accept="image/*"
                    />
                  </label>
                </div>
              )}
            </div>
            {newQuestion.type[0] === 'MCQ' && (
              <div className="mb-4">
                {[...Array(4)].map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    value={(newQuestion.mcq_options || [])[index] || ''}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.mcq_options];
                      newOptions[index] = e.target.value;
                      setNewQuestion({...newQuestion, mcq_options: newOptions});
                    }}
                    placeholder={`Option ${String.fromCharCode(97 + index)}`}
                    className="w-full p-2 border rounded mb-2"
                  />
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewQuestion}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Add Question
              </button>
            </div>
          </div>
        </div>
      )}

      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Share Assignment</h2>
            <p className="mb-4">Have you checked everything? Are you ready to share?</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsShareModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleShareAssignment}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Confirm Share
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Upload Answer Key</h2>
            <input 
              type="file" 
              onChange={handleFileUpload}
              accept=".doc,.docx,.pdf,.xls,.xlsx"
              className="mb-4"
            />
            {uploadedFile && (
              <p className="mb-4">Selected file: {uploadedFile.name}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUploadedFile}
                disabled={!uploadedFile || isUploading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

